import { useMutation, useQuery } from "@tanstack/react-query"
import { CalendarCheck } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import type {
  Periodo,
  Propuesta,
  VacacionProgramada,
} from "@/componentes/talento/planificacion/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { comoFecha } from "@/lib/fechas"

/**
 * Programar las vacaciones de un período: se elige el primer día y el servidor dice el
 * último, el reintegro y el pago.
 *
 * ⚠ **El cálculo lo hace el SERVIDOR, también la vista previa** (`/vacaciones/calculo`):
 * los feriados, los días hábiles y el redondeo viven en un solo sitio. Una segunda
 * cuenta aquí sería una segunda verdad sobre lo que se le paga a alguien.
 *
 * ⚠ **Programar emite la notificación de vacaciones** con el cálculo congelado; por eso
 * el botón lo dice, y al terminar se enlaza el documento.
 */
export function ProgramarVacaciones({
  personaId,
  periodo,
  onCerrar,
  onProgramada,
}: {
  personaId: number
  periodo: Periodo | null
  onCerrar: () => void
  onProgramada: () => void
}) {
  const [inicio, setInicio] = useState("")
  const cuerpo = { periodo: periodo?.periodo, inicio }

  const vista = useQuery({
    queryKey: ["vacaciones-calculo", personaId, periodo?.periodo, inicio],
    queryFn: () => api.post<Propuesta>(`/api/v1/personas/${personaId}/vacaciones/calculo`, cuerpo),
    enabled: periodo !== null && inicio !== "",
    retry: false,
  })

  const programar = useMutation({
    mutationFn: () =>
      api.post<VacacionProgramada>(`/api/v1/personas/${personaId}/vacaciones`, cuerpo),
    onSuccess: onProgramada,
  })

  function cerrar() {
    setInicio("")
    programar.reset()
    onCerrar()
  }

  const p = vista.data
  const filas: [string, string, string][] = p
    ? [
        ["Días hábiles (art. 190)", `${p.calculo.dias_habiles}`, p.calculo.monto_habiles],
        ["Descanso y feriados del período", `${p.calculo.dias_descanso}`, p.calculo.monto_descanso],
        ["Bono vacacional (art. 192)", `${p.calculo.dias_bono}`, p.calculo.monto_bono],
      ]
    : []

  return (
    <Modal
      abierto={periodo !== null}
      titulo={`Programar vacaciones ${periodo?.texto ?? ""}`}
      descripcion={
        periodo
          ? `${periodo.dias_habiles} días hábiles y ${periodo.dias_bono} de bono vacacional.`
          : undefined
      }
      onCerrar={cerrar}
    >
      {programar.isSuccess ? (
        <div className="space-y-3 text-sm">
          <p className="flex items-center gap-2" role="status">
            <CalendarCheck size={16} aria-hidden={true} style={{ color: "var(--success)" }} />
            Vacaciones programadas y notificación emitida.
          </p>
          {programar.data.documento_th_id !== null && (
            <Link
              className="underline"
              to={`/talento-humano/documento/${programar.data.documento_th_id}`}
            >
              Ver la notificación para imprimir y firmar
            </Link>
          )}
          <div className="flex justify-end">
            <Boton tamano="sm" onClick={cerrar}>
              Cerrar
            </Boton>
          </div>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium uppercase">
              Primer día de vacaciones
            </span>
            <input
              type="date"
              className="campo tabular-nums"
              value={inicio}
              min={periodo?.nace}
              onChange={(e) => setInicio(e.target.value)}
            />
          </label>

          {vista.isFetching && <p style={{ color: "var(--fg-muted)" }}>Calculando…</p>}
          {vista.isError && (
            <p role="alert" style={{ color: "var(--destructive)" }}>
              {vista.error instanceof ErrorApi ? vista.error.message : "No se pudo calcular"}
            </p>
          )}
          {p && !vista.isFetching && (
            <div className="superficie space-y-2 p-3">
              <p className="tabular-nums">
                Del <strong>{comoFecha(p.inicio)}</strong> al{" "}
                <strong>{comoFecha(p.ultimo_dia)}</strong> · se reintegra el{" "}
                <strong>{comoFecha(p.reintegro)}</strong>
              </p>
              <table className="w-full text-left">
                <tbody>
                  {filas.map(([concepto, dias, importe]) => (
                    <tr key={concepto}>
                      <td className="py-0.5 pr-2">{concepto}</td>
                      <td className="py-0.5 pr-2 text-right tabular-nums">{dias} d</td>
                      <td className="py-0.5 text-right tabular-nums">Bs. {monto(importe)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="pt-1">Total a pagar al inicio</td>
                    <td />
                    <td className="pt-1 text-right tabular-nums">Bs. {monto(p.calculo.total)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                Salario mensual Bs. {monto(p.calculo.salario_mensual)} · diario Bs.{" "}
                {monto(p.calculo.salario_diario)}. Los feriados decretados después no están
                contados.
              </p>
            </div>
          )}

          {programar.isError && (
            <p role="alert" style={{ color: "var(--destructive)" }}>
              {programar.error instanceof ErrorApi
                ? programar.error.message
                : "No se pudo programar"}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Boton tamano="sm" onClick={cerrar}>
              Cancelar
            </Boton>
            <Boton
              tamano="sm"
              variante="primario"
              disabled={!p || vista.isFetching || programar.isPending}
              onClick={() => programar.mutate()}
            >
              Programar y emitir la notificación
            </Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}
